import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_ADSENSE_CLIENT = "ca-pub-2943331157681506";
const SCRIPT_ID = "flexlearn-adsense-script";

function normalizeClient(clientId) {
  if (!clientId) return null;
  const trimmed = clientId.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("ca-pub-") ? trimmed : `ca-pub-${trimmed}`;
}

function isEligiblePublicPath(pathname) {
  if (pathname === "/" || pathname === "/pricing" || pathname === "/guides") {
    return true;
  }

  if (pathname.startsWith("/guides/")) {
    return true;
  }

  return [
    "/about",
    "/contact",
    "/disclaimer",
    "/terms",
    "/terms-and-conditions",
    "/privacy",
    "/privacy-policy",
    "/refund",
    "/refund-policy",
  ].includes(pathname);
}

export default function AdSenseRouteScript() {
  const location = useLocation();

  useEffect(() => {
    // AdSense for Content is web-only; native builds must not load it.
    if (Capacitor.isNativePlatform()) return;
    if (process.env.NODE_ENV !== "production") return;

    const configuredClient = normalizeClient(process.env.REACT_APP_ADSENSE_CLIENT);
    const client = configuredClient ?? DEFAULT_ADSENSE_CLIENT;

    if (!client) return;

    const eligible = isEligiblePublicPath(location.pathname);
    const existingScript = document.getElementById(SCRIPT_ID);

    if (!eligible) {
      if (existingScript) {
        existingScript.remove();
      }
      return;
    }

    if (existingScript) {
      return;
    }

    // Skip during react-snap pre-rendering so the heavy AdSense script is never
    // baked into the static HTML (it would then load on every visit before hydration).
    if (navigator.userAgent.includes("ReactSnap")) {
      return;
    }

    const inject = () => {
      if (document.getElementById(SCRIPT_ID)) return;
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
      document.head.appendChild(script);
    };

    // Defer off the critical path so AdSense doesn't compete for the main thread
    // during initial paint/hydration (improves Total Blocking Time).
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(inject, { timeout: 3000 })
      : window.setTimeout(inject, 2000);

    return () => {
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle);
      }
    };
  }, [location.pathname]);

  return null;
}
